import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { diagnosticsAPI, revolutAPI } from '../../lib/api';

/**
 * Guided card for correcting the Revolut /100 scaling bug.
 *
 * Sequence: Preview -> Re-sync bi_uk -> (auto re-preview) -> Apply (if residual rows).
 * Re-synced rows carry amount_source:"resync_v2" and are structurally excluded
 * from the corrective multiply, so over-correction is impossible.
 */
export function RevolutAmountCorrectionPanel() {
  const [buildCommit, setBuildCommit] = useState(null);
  const [preview, setPreview] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    diagnosticsAPI.buildInfo()
      .then(({ data }) => { if (!cancelled) setBuildCommit(data?.commit || 'unknown'); })
      .catch(() => { if (!cancelled) setBuildCommit('fetch failed'); });
    return () => { cancelled = true; };
  }, []);

  const runPreview = async () => {
    setPreviewLoading(true);
    setApplyResult(null);
    try {
      const res = await diagnosticsAPI.revolutAmountCorrectionPreview();
      setPreview(res.data);
    } catch {
      setPreview({ error: 'Failed to run preview.' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const runSync = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    setApplyResult(null);
    try {
      const res = await revolutAPI.sync();
      setSyncResult(res.data);
      // Auto re-preview so the correctable count updates
      try {
        const previewRes = await diagnosticsAPI.revolutAmountCorrectionPreview();
        setPreview(previewRes.data);
      } catch {
        // Preview refresh failed, keep previous preview
      }
    } catch {
      setSyncResult({ error: 'Failed to trigger Revolut sync.' });
    } finally {
      setSyncLoading(false);
    }
  };

  const runApply = async () => {
    const count = (preview?.transactions_to_correct || 0) + (preview?.accounts_to_correct || 0);
    if (!window.confirm(
      `Apply 100x correction to ${count} rows? ` +
      'Re-synced rows are structurally excluded. This cannot be undone.'
    )) return;
    setApplyLoading(true);
    try {
      const res = await diagnosticsAPI.revolutAmountCorrectionApply();
      setApplyResult(res.data);
      // Auto re-preview to confirm 0 remaining
      try {
        const previewRes = await diagnosticsAPI.revolutAmountCorrectionPreview();
        setPreview(previewRes.data);
      } catch {
        // Preview refresh failed, keep previous preview
      }
    } catch {
      setApplyResult({ error: 'Failed to apply corrections.' });
    } finally {
      setApplyLoading(false);
    }
  };

  const correctableCount = preview && !preview.error
    ? (preview.transactions_to_correct || 0) + (preview.accounts_to_correct || 0)
    : 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <CardTitle className="text-base">Revolut Amount Correction</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Corrects Revolut amounts stored 100x too small (the /100 scaling bug).
          Re-synced rows (amount_source=resync_v2) are structurally excluded.
          Sequence: Preview, Re-sync, Apply residuals.
        </p>
        <p className="text-xs font-medium text-muted-foreground mt-1">
          Target: BI UK GBP Main = &pound;34,143.28
        </p>
        {buildCommit && (
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-muted-foreground">Build:</span>
            <Badge variant="outline" className="text-[10px] font-mono">
              {buildCommit}
            </Badge>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={runPreview}
            disabled={previewLoading}
          >
            {previewLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Preview
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={runSync}
            disabled={syncLoading}
          >
            {syncLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Re-sync bi_uk
          </Button>

          <Button
            variant="default"
            size="sm"
            onClick={runApply}
            disabled={applyLoading || correctableCount === 0 || !!applyResult}
          >
            {applyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Apply {correctableCount > 0 ? `${correctableCount} Corrections` : '(none)'}
          </Button>
        </div>

        {/* Sync result */}
        {syncResult && (
          <div className={`p-2 rounded text-xs border ${syncResult.error ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
            {syncResult.error ? (
              <p className="text-red-600">{syncResult.error}</p>
            ) : (
              <p>
                Sync complete: {syncResult.accounts_synced || 0} accounts,{' '}
                {syncResult.transactions_synced || 0} transactions re-synced.
              </p>
            )}
          </div>
        )}

        {/* Preview results */}
        {preview && (
          <>
            {preview.error ? (
              <p className="text-xs text-red-600">{preview.error}</p>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-3 text-xs">
                  <span>
                    Correctable transactions:{' '}
                    <strong className={preview.transactions_to_correct > 0 ? 'text-amber-600' : 'text-green-600'}>
                      {preview.transactions_to_correct}
                    </strong>
                  </span>
                  <span>
                    Correctable accounts:{' '}
                    <strong className={preview.accounts_to_correct > 0 ? 'text-amber-600' : 'text-green-600'}>
                      {preview.accounts_to_correct}
                    </strong>
                  </span>
                </div>

                {/* Transaction samples */}
                {preview.transaction_samples?.length > 0 && (
                  <div className="border rounded overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left px-2 py-1 font-medium">Account</th>
                          <th className="text-left px-2 py-1 font-medium">Date</th>
                          <th className="text-right px-2 py-1 font-medium">Stored</th>
                          <th className="text-right px-2 py-1 font-medium">Corrected</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.transaction_samples.map((s, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="px-2 py-1 font-mono text-[11px]">{s.bank_account_id || s.id || '-'}</td>
                            <td className="px-2 py-1">{s.date ? s.date.slice(0, 10) : '-'}</td>
                            <td className="px-2 py-1 text-right font-mono">
                              {s.current_amount != null ? s.current_amount.toFixed(2) : '-'}
                            </td>
                            <td className="px-2 py-1 text-right font-mono font-medium">
                              {s.corrected_amount != null ? s.corrected_amount.toFixed(2) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Account samples */}
                {preview.account_samples?.length > 0 && (
                  <div className="border rounded overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left px-2 py-1 font-medium">Account</th>
                          <th className="text-left px-2 py-1 font-medium">Currency</th>
                          <th className="text-right px-2 py-1 font-medium">Stored Balance</th>
                          <th className="text-right px-2 py-1 font-medium">Corrected</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.account_samples.map((s, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="px-2 py-1">{s.account_name || s.id || '-'}</td>
                            <td className="px-2 py-1">{s.currency || '-'}</td>
                            <td className="px-2 py-1 text-right font-mono">
                              {s.current_balance != null ? s.current_balance.toFixed(2) : '-'}
                            </td>
                            <td className="px-2 py-1 text-right font-mono font-medium">
                              {s.corrected_balance != null ? s.corrected_balance.toFixed(2) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {correctableCount === 0 && (
                  <div className="flex items-center gap-1 text-xs text-green-600">
                    <CheckCircle2 className="h-3 w-3" />
                    No correctable rows remain. All Revolut amounts are current.
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Apply result */}
        {applyResult && (
          <div className={`p-3 rounded text-xs border ${applyResult.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            {applyResult.error ? (
              <p className="text-red-600">{applyResult.error}</p>
            ) : (
              <div className="space-y-1">
                <p>
                  <strong>Applied:</strong> {applyResult.transactions_corrected} transactions,{' '}
                  {applyResult.accounts_corrected} accounts corrected.
                </p>
                {(applyResult.tx_errors?.length > 0 || applyResult.acct_errors?.length > 0) && (
                  <p className="text-amber-600">
                    Errors: {applyResult.tx_errors?.length || 0} tx, {applyResult.acct_errors?.length || 0} acct
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
